import { useState } from 'react';
import { Badge, Button, Caption1, MessageBar, MessageBarBody, Radio, RadioGroup, Spinner, Text } from '@fluentui/react-components';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { Person, PersonStepSummary, StepState } from '@vigeo/shared';
import { attemptLabel, stepTypeLabel } from '@/features/review';
import { fetchPersonSteps } from '@/lib/api';
import { StepAnswersPanel } from '../StepAnswersPanel.js';
import { usePersonStyles } from './styles.js';

/**
 * STEP 5 — REVIEW. Frame `277:6308`, read from the frame itself.
 *
 * NOT A SECOND REVIEW QUEUE: the frame ends with "The Review queue in the sidebar lists items
 * waiting across every candidate." That one is where decisions are made, across everybody; this is
 * the one candidate's side of it, which the record could not show at all — step 4 lists every form
 * SENT, and nothing listed what came BACK.
 *
 * GROUPED BY STATUS, NOT BY PORTAL GROUP. The first build of this step grouped by the 3.1 form
 * groups, because the handoff's one-line summary says "grouped the same way" and I read that as
 * step 4's grouping without opening the frame. The frame groups by **Awaiting review**, **Sent
 * back — waiting on <first name>** and **Approved**, each with a count beside the heading. The
 * grouping is the screen's whole shape, so that was not a cosmetic miss.
 *
 * WHAT COUNTS AS SUBMITTED IS `submittedAt`, NOT THE STATE. A sent-back step returns to
 * `in_progress` the moment the candidate touches it (`portal.ts`, the `reopening` branch), keeping
 * its `submittedAt` and incrementing its attempt. Filtering on state alone would drop it exactly
 * when the candidate picks it up, which is when HR most wants to see it is in flight.
 */

export interface ReviewStepProps {
  readonly person: Person;
  readonly canEdit: boolean;
}

const FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'awaiting', label: 'Awaiting review' },
  { id: 'sent-back', label: 'Sent back' },
  { id: 'approved', label: 'Approved' },
] as const;
type FilterId = (typeof FILTERS)[number]['id'];

/** The chip beside a row. Exhaustive by type, so a new kernel state stops this compiling. */
const CHIP: Record<StepState, { readonly label: string; readonly tone: 'informative' | 'warning' | 'success' | 'danger' }> = {
  not_started: { label: 'Not started', tone: 'informative' },
  sent: { label: 'Sent, not opened', tone: 'informative' },
  in_progress: { label: 'Being redone', tone: 'informative' },
  submitted: { label: 'Awaiting review', tone: 'warning' },
  approved: { label: 'Approved', tone: 'success' },
  rejected: { label: 'Sent back', tone: 'danger' },
  cancelled: { label: 'Cancelled', tone: 'informative' },
};

/** The verb the frame prints before the date, per state. */
const VERB: Partial<Record<StepState, string>> = {
  submitted: 'submitted',
  approved: 'approved',
  rejected: 'sent back',
  in_progress: 'sent back',
};

const submitted = (step: PersonStepSummary) => step.submittedAt !== null;
const formatDate = (iso: string) => new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });

function matches(step: PersonStepSummary, filter: FilterId): boolean {
  if (filter === 'all') return true;
  if (filter === 'awaiting') return step.state === 'submitted';
  if (filter === 'sent-back') return step.state === 'rejected';
  return step.state === 'approved';
}

/**
 * The frame's second line: the step type, then what last happened and when, then the attempt.
 *
 * The attempt is spelled out ("First attempt"), which is the review queue's own `attemptLabel` —
 * so a submission reads the same here as it does in the queue HR is sent to.
 */
function detailLine(step: PersonStepSummary): string {
  const when = step.reviewedAt ?? step.submittedAt;
  const verb = VERB[step.state] ?? 'submitted';
  const parts = [stepTypeLabel(step.type)];
  if (when !== null) parts.push(`${verb} ${formatDate(when)}`);
  parts.push(attemptLabel(step.attempt).toLowerCase());
  return parts.join(' · ');
}

export function ReviewStep({ person, canEdit }: ReviewStepProps) {
  const styles = usePersonStyles();
  const [filter, setFilter] = useState<FilterId>('all');
  const [open, setOpen] = useState<string | null>(null);
  const first = person.name.preferredName || person.name.givenName;
  const steps = useQuery({ queryKey: ['person-steps', person.id], queryFn: () => fetchPersonSteps(person.id) });

  // The API has already applied `orderPortalForms`; re-sorting here would be a second opinion.
  const all = (steps.data ?? []).filter(submitted);
  const shown = all.filter((step) => matches(step, filter));
  const counts = (id: FilterId) => all.filter((step) => matches(step, id)).length;

  /*
   * THE FRAME'S THREE GROUPS, in its order, plus one it does not draw.
   *
   * A step sent back and already picked up again is `in_progress`; the frame's fixture has no such
   * row, so it names no group for it. It gets its own rather than being folded into "Sent back",
   * which would show HR items the candidate has already started fixing. Flagged for Paul.
   */
  const groups = [
    { id: 'awaiting', heading: 'Awaiting review', rows: shown.filter((step) => step.state === 'submitted') },
    { id: 'sent-back', heading: `Sent back — waiting on ${first}`, rows: shown.filter((step) => step.state === 'rejected') },
    { id: 'redoing', heading: `Sent back — ${first} is redoing these`, rows: shown.filter((step) => step.state === 'in_progress') },
    { id: 'approved', heading: 'Approved', rows: shown.filter((step) => step.state === 'approved') },
  ].filter((group) => group.rows.length > 0);

  return <div className={styles.page}>
    {/* The frame's "Review" title and its sentence are the section's own `label` and
        `description`, drawn by `SectionWorkspace`; repeating them here printed the word twice. */}
    <section className={styles.card} aria-label="Submissions">
      {/*
        RADIO, not a row of toggle buttons: the frame draws selection circles, and exactly one
        filter is active at a time — which is what a radio group tells a screen reader and a row
        of `aria-pressed` buttons does not.
      */}
      <RadioGroup layout="horizontal" className={styles.filterGroup} value={filter} aria-label="Filter submissions"
        onChange={(_event, data) => setFilter(data.value as FilterId)}>
        {FILTERS.map((entry) => <Radio key={entry.id} value={entry.id} label={`${entry.label} (${counts(entry.id)})`} />)}
      </RadioGroup>

      {steps.isPending ? <Spinner size="tiny" label="Reading what this person has sent in" />
        : steps.isError ? <MessageBar intent="error"><MessageBarBody>The submissions could not be read. {steps.error.message}</MessageBarBody></MessageBar>
          : all.length === 0 ? <Text>{`${first} has not sent anything in yet.`}</Text>
            : shown.length === 0 ? <Text>Nothing is in that state right now.</Text>
              : groups.map((group) => (
                <section key={group.id} aria-label={group.heading}>
                  <h4 className={styles.h}>{`${group.heading} (${group.rows.length})`}</h4>
                  <ul className={styles.recordList}>
                    {group.rows.map((step) => (
                      <li key={step.id}>
                        <div className={styles.recordRow}>
                          <div className={styles.recordMain}>
                            <Text weight="semibold">{step.heading ?? stepTypeLabel(step.type)}</Text>
                            <Caption1>{detailLine(step)}</Caption1>
                          </div>
                          <div className={styles.recordAside}>
                            <Badge appearance="outline" shape="rounded" color={CHIP[step.state].tone}>{CHIP[step.state].label}</Badge>
                            {/*
                              REVIEW GOES TO THE QUEUE, deep-linked to this submission, because
                              that is where the approve and send-back controls live and the frame
                              keeps it as the cross-candidate list. A viewer gets View instead:
                              deciding is `review.decide`, which they do not hold.
                            */}
                            {step.state === 'submitted' && canEdit
                              ? <Link to={`/app/review?instance=${encodeURIComponent(step.id)}`}>Review</Link>
                              : <Button appearance="subtle" size="small" aria-expanded={open === step.id}
                                  onClick={() => setOpen(open === step.id ? null : step.id)}>
                                  {open === step.id ? 'Hide' : 'View'}
                                </Button>}
                          </div>
                        </div>
                        {open === step.id
                          ? <StepAnswersPanel personId={person.id} instanceId={step.id} type={step.type} config={step.config} candidateFirstName={first} />
                          : null}
                      </li>
                    ))}
                  </ul>
                </section>
              ))}

      {/* The frame's own closing line, so the two Review screens are not mistaken for each other. */}
      <Caption1>The Review queue in the sidebar lists items waiting across every candidate.</Caption1>
    </section>
  </div>;
}
